import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, type LegalEntity } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import { HomeCards } from '../components/home-cards.tsx';
import { Field, Select } from '../components/ui.tsx';
import { setFocusEntity, useFocusEntity } from '../focus.ts';
import { DEPARTMENT_CARDS } from './home-cards.ts';
import { PurchaseHomeIndicators } from './purchasing/HomeIndicators.tsx';

/** Frentes com cartões, depois de Compras, que tem o bloco próprio do ADR 0095. */
export const EXECUTIVE_FRONTS = ['finance', 'fpa', 'legal', 'facilities', 'it', 'system'] as const;

/** Olhar uma empresa por vez (ADR 0100). Com uma empresa só, não há o que escolher. */
function FocusSelect() {
  const { t } = useTranslation();
  const me = useMe();
  const focus = useFocusEntity();
  const entities = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
    enabled: can(me.data, 'core.legal_entity.read'),
  });
  const active = (entities.data ?? []).filter((e) => e.isActive);
  if (active.length < 2) return null;
  const choose = (id: string) => {
    const entity = active.find((e) => e.id === id);
    setFocusEntity(entity ? { id: entity.id, name: entity.tradeName ?? entity.legalName } : null);
  };
  return (
    <Field label={t('focus.label')} className="max-w-sm">
      <Select value={focus?.id ?? ''} onChange={(e) => choose(e.target.value)}>
        <option value="">{t('focus.all')}</option>
        {active.map((e) => (
          <option key={e.id} value={e.id}>
            {e.tradeName ?? e.legalName}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * Dashboard executivo (ADR 0097): os mesmos cartões das Visões gerais, por frente. Nada é contado
 * aqui: cada frente responde pela rota dela, e a pessoa só recebe o que já pode abrir.
 */
export function ExecutiveDashboard() {
  const { t } = useTranslation();
  return (
    <>
      <FocusSelect />
      <PurchaseHomeIndicators title={t('areas.purchasing')} />
      {EXECUTIVE_FRONTS.map((id) => {
        const config = DEPARTMENT_CARDS[id];
        return config && <HomeCards key={id} config={config} title={t(`areas.${id}`)} />;
      })}
    </>
  );
}
